import {useEffect, useRef} from "react";
import {Outlet} from "react-router-dom";

import {useDynamicAppTitle} from "../hooks/appTitleHook";
import {useNavigationPending, useQueryParam} from "../hooks/navigationHooks";
import {selectDevelopMode} from "../redux/envSlice";
import {
    selectLoadedSnapshotNumber,
    selectPersistenceInitialized,
    selectRevaId,
    setInitialized,
} from "../redux/persistenceSlice";
import {fetchPersistence} from "../redux/persistenceThunk";
import {useRevaDispatch, useRevaSelector} from "../redux/revaHooks";
import {buildValuationId} from "../util/valuationId";
import Header from "./Header";
import {GlobalNotifications} from "./Notifications";
import {RevaOrderTypeNotEnabledAdminWarning} from "./RevaOrderTypeNotEnabled";
import {Sidebar} from "./Sidebar";
import PurposeChoiceDialog from "./start/PurposeChoiceDialog";

function App() {
    const dispatch = useRevaDispatch();

    const isDevelop = useRevaSelector(selectDevelopMode);
    const persistenceInitialized = useRevaSelector(selectPersistenceInitialized);
    const persistenceInitializing = useRef(false);
    const revaId = useRevaSelector(selectRevaId);
    const loadedSnapshotNumber = useRevaSelector(selectLoadedSnapshotNumber);
    // The id reflected in the URL: the bare RevaId, or "<RevaId>:<snapshotNumber>" while a snapshot is
    // loaded, so a reload/bookmark re-loads the same snapshot.
    const effectiveId = revaId == null ? null : buildValuationId(revaId, loadedSnapshotNumber);

    const [idQueryParam, setIdQueryParam] = useQueryParam("id");
    const [orderQueryParam] = useQueryParam("order");
    const [clientQueryParam] = useQueryParam("client");
    const navigationPending = useNavigationPending();

    // adapt idQueryParam to the loaded valuation (incl. snapshot suffix, see effectiveId)
    useEffect(() => {
        // Not while a navigation is pending: setIdQueryParam navigates to window.location.pathname,
        // which still names the page being left - that would abort the pending navigation and pull
        // the app back to where it came from.
        if (navigationPending()) return;
        if (!persistenceInitialized) return;

        const shouldSetId = idQueryParam !== effectiveId;

        if (shouldSetId) {
            setIdQueryParam(
                effectiveId,
                /* interceptHandleParams */ (newParams) => {
                    // Clear previously entered order/client query parameters, after RevaID was loaded.
                    // Must be done in a single navigation, otherwise the URL switches in an endless loop
                    newParams.delete("order");
                    newParams.delete("client");
                },
            );
        }
    }, [navigationPending, idQueryParam, effectiveId, setIdQueryParam, persistenceInitialized]);

    // load initial revaId from query param
    useEffect(() => {
        if (persistenceInitialized || persistenceInitializing.current) {
            return;
        }
        persistenceInitializing.current = true;

        if (idQueryParam) {
            void dispatch(fetchPersistence({id: idQueryParam}));
            return;
        }
        if (orderQueryParam && clientQueryParam) {
            void dispatch(fetchPersistence({orderId: orderQueryParam, clientId: clientQueryParam}));
            return;
        }
        dispatch(setInitialized());
        if (isDevelop) {
            console.log("No persistence found");
        }
    }, [idQueryParam, orderQueryParam, clientQueryParam, dispatch, persistenceInitialized, isDevelop]);

    // Activate hook to always change the title in the browser according to currently loaded valuation
    useDynamicAppTitle();

    return (
        <>
            <GlobalNotifications />
            {/* #19481: Zweckwahl vor dem Erstimport eines Auftrags aus EVA+ (?order=&client=) */}
            <PurposeChoiceDialog pending="order" />
            <Header />
            <RevaOrderTypeNotEnabledAdminWarning />
            <div className="MainView grow shrink-0">
                <div className="flex flex-row w-full h-full">
                    <Sidebar />
                    <Outlet />
                </div>
            </div>
        </>
    );
}

export default App;
